import { assertIsDefined, filterOutNullable } from '@gitbook/util-ts';

import { SuspenseCacheEntryState, type SuspenseCacheResult } from './cache';

export type MaybePromise<T> = T extends Promise<infer E> ? MaybePromise<E> : T | Promise<T>;

/**
 * Get a value from a suspender.
 */
export function getSuspense<T>(value: MaybePromise<T>): T {
    if (value instanceof Promise) {
        throw value;
    }

    return value as T;
}

/**
 * Get a value from a suspender, and ignore error.
 */
export function getSuspenseSafe<T>(fn: () => MaybePromise<T>): T | undefined {
    try {
        return getSuspense(fn());
    } catch (error) {
        if (error instanceof Promise) {
            throw error;
        }

        return undefined;
    }
}

/**
 * Convert a suspense state to a MaybePromise.
 */
export function suspenseToMaybePromise<T>(state: SuspenseCacheResult<T>): MaybePromise<T> {
    if (state.state === SuspenseCacheEntryState.PENDING) {
        // @ts-ignore
        return state.pending;
    }
    if (state.state === SuspenseCacheEntryState.FAILED) {
        throw state.error;
    }

    // @ts-ignore
    return state.result;
}

/**
 * Transform a value in a MaybePromise.
 */
export function thenMaybePromise<T, ReturnT>(
    value: MaybePromise<T>,
    transform: (value: T) => ReturnT
): MaybePromise<ReturnT> {
    if (value instanceof Promise) {
        return value.then(transform) as MaybePromise<ReturnT>;
    }

    return transform(value as T) as MaybePromise<ReturnT>;
}

/**
 * Wait for all maybe promises to resolve.
 */
export function maybePromiseAll<T extends readonly unknown[] | [], ReturnT>(
    values: T,
    callback: (value: { -readonly [P in keyof T]: Awaited<T[P]> }) => ReturnT
): MaybePromise<ReturnT> {
    if (values.some((value) => (value as any) instanceof Promise)) {
        return Promise.all(values).then(callback) as MaybePromise<ReturnT>;
    }
    return callback(values as { -readonly [P in keyof T]: Awaited<T[P]> }) as MaybePromise<ReturnT>;
}

/**
 * Execute a function that can suspend into an async manner.
 */
export function executeSuspenseAsync<Result>(fn: () => Result): Promise<Result> {
    try {
        const result = fn();
        return Promise.resolve(result);
    } catch (catched) {
        if (catched instanceof Promise) {
            return catched.then(() => executeSuspenseAsync(fn));
        }

        throw catched;
    }
}

/**
 * Map over an array using a callback that can suspend rendering,
 * if multiple items suspend rendering a promise for all items will be thrown.
 *
 * This function is useful to concurrently initialize an array depending on suspender
 */
export function mapSuspenseArray<Input, Result>(
    inputs: readonly Input[],
    fn: (item: Input, index: number, items: readonly Input[]) => Result
): Result[] {
    const pending: Array<Promise<any>> = [];
    const results = inputs
        .map((item, index, items) => {
            try {
                const result = fn(item, index, items);
                return result;
            } catch (error) {
                if (error instanceof Promise) {
                    pending.push(error);
                    return null;
                }
                throw error;
            }
        })
        .filter(filterOutNullable);

    if (pending.length > 0) {
        throw Promise.all(pending);
    }

    return results;
}

/**
 * Concurrently execute functions that can suspend into an async manner.
 */
export function executeSuspenseAll<Fns extends readonly (() => any)[]>(
    fns: Fns
): {
    [key in keyof Fns]: ReturnType<Fns[key]>;
} {
    const pending: Array<Promise<any>> = [];
    const results: Array<ReturnType<Fns[number]>> = [];

    for (let i = 0; i < fns.length; i++) {
        const fn = fns[i];
        try {
            assertIsDefined(fn);
            const result = fn();
            results.push(result);
        } catch (error) {
            if (error instanceof Promise) {
                pending.push(error);
            } else {
                throw error;
            }
        }
    }

    if (pending.length > 0) {
        throw Promise.all(pending);
    }

    // @ts-ignore
    return results;
}
